import React from 'react';
import styled from 'styled-components';
import * as PropTypes from 'prop-types';
import { Tooltip as StrapTooltip } from 'reactstrap';

const StyledTooltip = styled(StrapTooltip)`
  .tooltip {
    pointer-events: none;
  }
`;

class Tooltip extends React.Component {
  constructor(props) {
    super(props);
    this.toggle = this.toggle.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.state = {
      tooltipOpen: false,
      hidden: null
    };
    this._hideTimeoutRef = null;
  }

  /**
   * Hides tooltip if it is open and returns true. Else returns false.
   *
   * @param tipId
   * @returns {boolean}
   */
  hideIfOpen(tipId) {
    const { tooltipOpen } = this.state;

    if (tooltipOpen) {
      this.toggle();
      this.setState({ hidden: tipId });
      return true;
    }

    return false;
  }

  /**
   * When we receive a mousedown event and a tooltip is open or is about to open
   * we hide it. The tooltip could be blocking a modal or generally messing up
   * with the user experience e.g. a modal opens and the tooltip is still
   * visible. When we receive a mousedown and a tooltip is hidden we
   * un-hide it i.e. when the user hovers they can see it again.
   */
  handleMouseDown() {
    const { id } = this.props;
    const { hidden } = this.state;

    if (hidden && id === hidden) {
      this.setState({ hidden: null });
    } else {
      if (!this.hideIfOpen(id)) {
        // take care of tooltip that is about to load - coz of show delay
        this._hideTimeoutRef = setTimeout(() => this.hideIfOpen(id), 500);
      }
    }
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleMouseDown);
    if (this._hideTimeoutRef) {
      clearTimeout(this._hideTimeoutRef);
    }
  }

  toggle() {
    this.setState({
      tooltipOpen: !this.state.tooltipOpen
    });
  }

  /**
   * Determine whether tooltip should render based on a condition provided by
   * the user.
   */
  isVisible() {
    const { visible } = this.props;

    return typeof visible === 'function' ? visible() : visible;
  }

  /**
   * Determine whether the tooltip should render.
   * - jsdom does not work correctly with our selector [aria-describedby="tip-${id}"]
   * - rendering the tooltip during tests might also break visual tests
   * - if we have no target id and tooltip title we cannot render it
   * - if the tooltip is marked as hidden we cannot render it
   */
  shouldRender() {
    const { hidden } = this.state;
    const { id, title } = this.props;
    const { NODE_ENV } = process.env;

    return (
      this.isVisible() && NODE_ENV !== 'test' && id && title && id !== hidden
    );
  }

  render() {
    const { tooltipOpen } = this.state;
    const { id, title, placement } = this.props;

    if (!this.shouldRender()) {
      return null;
    }

    return (
      <StyledTooltip
        modifiers={[
          {
            name: 'preventOverflow',
            options: {
              boundary: 'window'
            }
          }
        ]}
        placement={placement}
        isOpen={tooltipOpen}
        target={`[aria-describedby="tip-${id}"]`}
        toggle={this.toggle}
        trigger="hover"
        fade={false}
        delay={{ show: 500, hide: 250 }}
        role="tooltip"
      >
        {title}
      </StyledTooltip>
    );
  }
}

Tooltip.defaultProps = {
  id: null,
  title: null,
  visible: true,
  placement: 'bottom'
};

export const tooltipPropTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  visible: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  placement: PropTypes.string
};

Tooltip.propTypes = tooltipPropTypes;

export default Tooltip;

////////////////////////////////////////////////////////////////////////
// The functions below are useful when adding a tooltip to a reusable //
// component. This is because the reusable component may be used in   //
// different sections of the application requiring different props.   //
// If the tooltip is not desired, failing to pass the tooltip props   //
// to the component does not break anything.                          //
////////////////////////////////////////////////////////////////////////

/**
 * Determines whether the object has the props required to render a tooltip
 * component.
 *
 * @param props
 * @returns {*}
 */
const hasRequiredProps = props => {
  const { id, title } = props || {};

  return id && title;
};

/**
 * Returns an object with aria-describedby if the props have the required
 * fields. Else returns an empty object.
 *
 * @returns {*}
 * @param props
 */
export const getTooltipTarget = props => {
  return hasRequiredProps(props)
    ? { 'aria-describedby': `tip-${props.id}` }
    : {};
};
